import { useCallback, useEffect, useState } from "react";
import type {
  CustomerPortalPeople,
  CustomerPortalPeopleAdapter,
} from "../data/customer-portal-access-types";

const NO_TEAMS: readonly string[] = [];

export type PortalPeopleState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: CustomerPortalPeople };

/**
 * One customer's portal people, with `reload` that refetches without blanking
 * the page: after granting an extra or adding a person the table stays put and
 * updates, instead of flashing back to a skeleton.
 *
 * Takes the adapter as an argument because the two shells get it differently: the
 * staff app from its `DataAdapter`, the portal's staff view from its own context.
 */
export function usePortalPeople(
  adapter: CustomerPortalPeopleAdapter,
  customerId: string,
  teamIds: readonly string[] = NO_TEAMS,
): {
  state: PortalPeopleState;
  reload: () => void;
} {
  const [state, setState] = useState<PortalPeopleState>({ status: "loading" });
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    // Only the first load (or a change of customer) shows the loading state.
    setState((prev) =>
      prev.status === "ready" && prev.data.customer_id === customerId
        ? prev
        : { status: "loading" },
    );
    adapter
      .getCustomerPortalPeople(
        customerId,
        teamIds.length > 0 ? { teamIds } : undefined,
      )
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setState({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
          });
      });
    return () => {
      cancelled = true;
    };
    // The ids' content, not the array's identity, asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, customerId, version, teamIds.join(" ")]);

  return { state, reload };
}
